import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSetBreadcrumbs, type BreadcrumbEntry } from "@/shared/contexts/breadcrumb-context";
import { ALERTS_URL } from "@/alerts/config/alerts-urls";
import { useOrganizationStore } from "@/shared/stores/useOrganizationStore.ts";
import { useGetOrganizationById } from "@/auth/api/queries/user.queries.ts";
import { useGetAllLocations } from "@/locations/api/queries/location.queries.ts";

interface UseAlertsBreadcrumbsArgs {
  /**
   * The scope the mode is showing. Each mode owns its own location (the Queue
   * and History persist theirs in the workspace store, Analytics holds local
   * state), so it is passed in rather than read here — publishing one mode's
   * scope while another is on screen would name the wrong site.
   *
   * Omit on modes with no location at all (Ingest), which get `Org › Alerts`.
   */
  locationId?: number | null;
  /**
   * Applies a pick from the location dropdown. MUST be identity-stable:
   * `useSetBreadcrumbs` diffs entries with `JSON.stringify`, which drops
   * functions, so an unstable callback is never re-published and the crumb goes
   * on calling the first render's closure. Zustand actions and `useState`
   * setters already satisfy this; anything else needs a ref-backed `useCallback`.
   */
  onSelectLocation?: (id: number) => void;
}

/**
 * Publishes the Alerts workspace trail: Organization › Alerts › Location.
 *
 * Same shape as the map's (`useMapBreadcrumbs`), and for the same reason: the
 * org crumb is the standard route-based switcher, while the LOCATION crumb
 * applies its pick in-page via `onSelect` — the workspace holds its scope in
 * store/component state, not in the URL, so there is no id in the path to swap.
 *
 * The mode (Queue / History / Analytics / Ingest) is deliberately NOT a crumb:
 * it is already the tab bar directly beneath, and naming it twice would make the
 * trail longer without making it say anything new.
 *
 * Org and location names are read here rather than passed in, so each page needs
 * one line. Both come from queries the workspace already runs, so they are cache
 * hits, not extra requests.
 */
export const useAlertsBreadcrumbs = ({
  locationId,
  onSelectLocation,
}: UseAlertsBreadcrumbsArgs = {}) => {
  const { t } = useTranslation("alerts");
  const selectedOrganizationId = useOrganizationStore((s) => s.selectedOrganizationId);
  const { data: orgData, isLoading: isOrgLoading } = useGetOrganizationById(
    selectedOrganizationId!,
  );
  const locations = useGetAllLocations().data?.data;
  const locationName = locationId == null
    ? undefined
    : locations?.find((l) => l.id === locationId)?.name;

  useSetBreadcrumbs(
    useMemo(() => {
      const crumbs: BreadcrumbEntry[] = [
        {
          path: ALERTS_URL.ROOT,
          breadcrumb: orgData?.data?.name || t("breadcrumbs.organization"),
          type: "organizations",
          currentId: selectedOrganizationId!,
          isLoading: isOrgLoading,
        },
        { path: ALERTS_URL.ROOT, breadcrumb: t("breadcrumbs.alerts") },
      ];
      // Only once the name has resolved: a crumb reading the raw id (or an empty
      // string) is worse than no crumb, and the list lands a moment after mount.
      if (locationId != null && locationName) {
        crumbs.push({
          path: ALERTS_URL.ROOT,
          breadcrumb: locationName,
          type: "locations",
          currentId: locationId,
          onSelect: onSelectLocation,
        });
      }
      return crumbs;
    }, [
      orgData?.data?.name,
      selectedOrganizationId,
      isOrgLoading,
      locationId,
      locationName,
      onSelectLocation,
      t,
    ]),
  );
};
